import { Button, Card } from '@shared/components/ui';
import { ArrowLeft, ArrowRight, Check, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WizardSteps } from './WizardSteps';
import { Credentials } from './wizard/Credentials';
import { Name } from './wizard/Name';
import { Preview } from './wizard/Preview';
import { AlertSettings } from './wizard/AlertSettings';
import { Retention } from './wizard/Retention';
import { Schedule } from './wizard/Schedule';
import { SourceSelection } from './wizard/SourceSelection';
import { Storage } from './wizard/Storage';
import { useUser } from '@shared/hooks/useUser';
import { useDialog } from '@shared/hooks/useDialog';

interface BackupConfigWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editConfigId?: string;
  initialAgentId?: string;
}

const getSteps = (type: 'POSTGRESQL' | 'FILES', t: (key: string) => string) => [
  { id: 'name', label: t('wizard.steps.name') },
  { id: 'source', label: t('wizard.steps.source') },
  {
    id: 'credentials',
    label: type === 'FILES' ? t('wizard.steps.fileSource') : t('wizard.steps.credentials'),
  },
  { id: 'schedule', label: t('wizard.steps.schedule') },
  { id: 'storage', label: t('wizard.steps.storage') },
  { id: 'retention', label: t('wizard.steps.retention') },
  { id: 'alerts', label: t('wizard.steps.alerts') },
  { id: 'preview', label: t('wizard.steps.preview') },
];

type StepId = ReturnType<typeof getSteps>[number]['id'];

interface WizardData {
  name: string;
  type: 'POSTGRESQL' | 'FILES';
  agent_id: string;
  created_by?: string;
  host?: string;
  port?: number;
  username?: string;
  password?: string;
  database?: string;
  databases?: string[];
  usePgDumpAll?: boolean;
  backupGlobalsOnly?: boolean;
  paths?: string[]; // Multiple paths for file backups
  useVSS?: boolean; // Windows Volume Shadow Copy Service for file backups
  schedule_type: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'MINUTELY' | 'CUSTOM';
  time?: string;
  day_of_week?: number;
  day_of_month?: number;
  interval_minutes?: number;
  cron_expression?: string;
  timezone?: string;
  storage_destination_id: string;
  storage_destination_name?: string;
  agent_name?: string;
  run_missing_jobs_on_startup: boolean;
  retention_policy?: {
    keep_daily?: number;
    keep_weekly?: number;
    keep_monthly?: number;
    keep_last?: number;
  };
  alert_settings?: {
    grace_period_minutes?: number;
    throttle_interval_minutes?: number;
  };
}

const createInitialWizardData = (agentId = ''): WizardData => ({
  name: '',
  type: 'POSTGRESQL',
  agent_id: agentId,
  schedule_type: 'DAILY',
  storage_destination_id: '',
  run_missing_jobs_on_startup: false,
  retention_policy: {
    keep_daily: 7,
    keep_weekly: 4,
    keep_monthly: 6,
    keep_last: 0,
  },
  alert_settings: {
    grace_period_minutes: 10,
    throttle_interval_minutes: 60,
  },
});

export function BackupConfigWizard({
  isOpen,
  onClose,
  onSuccess,
  editConfigId,
  initialAgentId = '',
}: BackupConfigWizardProps) {
  const { t } = useTranslation('backups');
  const { showError } = useDialog();
  const [currentStep, setCurrentStep] = useState<StepId>('source');
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set([0]));
  const [loading, setLoading] = useState(false);
  const [fetchingConfig, setFetchingConfig] = useState(false);
  const { user } = useUser();
  const [data, setData] = useState<WizardData>(() => createInitialWizardData(initialAgentId));
  const [initialDataSnapshot, setInitialDataSnapshot] = useState<string | null>(null);

  const isEditMode = !!editConfigId;
  const hasUnsavedChanges =
    initialDataSnapshot !== null && JSON.stringify(data) !== initialDataSnapshot;

  // Reset wizard to first step when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('name');
      setCompletedSteps(new Set([0]));
      setInitialDataSnapshot(null);
      if (!isEditMode) {
        setData(createInitialWizardData(initialAgentId));
      }
    }
  }, [initialAgentId, isEditMode, isOpen]);

  // Fetch config data when in edit mode
  useEffect(() => {
    if (isEditMode && isOpen) {
      setFetchingConfig(true);
      const token = localStorage.getItem('auth_token');
      fetch(`/api/v1/backups/configs/${editConfigId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then(async (config) => {
          // Map schedule from backend format to wizard format
          let scheduleType = 'DAILY';
          let time = '';
          let dayOfWeek: number | undefined;
          let dayOfMonth: number | undefined;
          let intervalMinutes: number | undefined;
          let cronExpr = '';

          if (config.schedule) {
            const backendType = (config.schedule.type || '').toUpperCase();
            const backendCron = config.schedule.cron_expression;
            const interval = config.schedule.interval_value;

            if (backendType === 'CRON' && backendCron) {
              // Parse cron to determine if it's DAILY, WEEKLY, or CUSTOM
              const parts = backendCron.split(' ');
              if (parts.length === 5) {
                const [min, hour, day, month, dow] = parts;

                // Check for complex cron expressions (lists, ranges, steps) that we can't handle
                const hasComplexValues = [min, hour, day, month, dow].some(
                  (part) =>
                    part.includes(',') || part.includes('/') || (part.includes('-') && part !== '*')
                );

                if (hasComplexValues) {
                  scheduleType = 'CUSTOM';
                  cronExpr = backendCron;
                } else if (day === '*' && month === '*' && dow === '*') {
                  scheduleType = 'DAILY';
                  time = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
                } else if (day === '*' && month === '*' && dow !== '*') {
                  scheduleType = 'WEEKLY';
                  time = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
                  dayOfWeek = parseInt(dow);
                } else if (day !== '*' && month === '*' && dow === '*') {
                  scheduleType = 'MONTHLY';
                  time = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
                  dayOfMonth = parseInt(day);
                } else {
                  scheduleType = 'CUSTOM';
                  cronExpr = backendCron;
                }
              } else {
                scheduleType = 'CUSTOM';
                cronExpr = backendCron;
              }
            } else if (backendType === 'INTERVAL' && interval) {
              // Interval type - check if it's minute-based
              const match = interval.match(/^(\d+)m$/);
              if (match) {
                scheduleType = 'MINUTELY';
                intervalMinutes = parseInt(match[1]);
              } else {
                scheduleType = 'CUSTOM';
                cronExpr = interval;
              }
            } else if (backendType === 'ONETIME') {
              scheduleType = 'CUSTOM';
            }
          }

          // Handle paths - backend stores as 'paths' array
          const filePaths =
            config.type === 'FILES'
              ? config.config?.paths || [config.config?.path].filter(Boolean) || []
              : [];

          // Fetch storage destination name if we have an ID
          let storageDestName = '';
          if (config.storage_destination_id) {
            try {
              const destRes = await fetch(`/api/v1/storage/${config.storage_destination_id}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (destRes.ok) {
                const destData = await destRes.json();
                storageDestName = destData.name || '';
              }
            } catch (_e) {
              console.error('Failed to fetch storage destination name:', _e);
            }
          }

          // Fetch alert settings if available
          let alertSettings = {
            grace_period_minutes: 10,
            throttle_interval_minutes: 60,
          };
          try {
            const alertRes = await fetch(`/api/v1/backups/configs/${editConfigId}/alert-settings`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (alertRes.ok) {
              const alertData = await alertRes.json();
              alertSettings = {
                grace_period_minutes: alertData.grace_period_minutes || 10,
                throttle_interval_minutes: alertData.throttle_interval_minutes || 60,
              };
            }
          } catch (e) {
            console.error('Failed to fetch alert settings:', e);
          }

          // Normalize type to uppercase for frontend (backend returns lowercase)
          const configType = (config.type || 'postgresql').toUpperCase();

          const newData: WizardData = {
            name: config.name || '',
            type: configType as 'POSTGRESQL' | 'FILES',
            agent_id: config.agent_id || '',
            created_by: config.created_by || '',
            storage_destination_id: config.storage_destination_id || '',
            storage_destination_name: storageDestName,
            run_missing_jobs_on_startup: config.run_missing_jobs_on_startup ?? false,
            schedule_type: scheduleType as WizardData['schedule_type'],
            time,
            day_of_week: dayOfWeek,
            day_of_month: dayOfMonth,
            interval_minutes: intervalMinutes,
            cron_expression: cronExpr,
            retention_policy: config.retention_policy || {
              keep_daily: 7,
              keep_weekly: 4,
              keep_monthly: 6,
              keep_last: 0,
            },
            alert_settings: alertSettings,
            ...(configType === 'POSTGRESQL'
              ? {
                  host: config.config?.host,
                  port: config.config?.port,
                  username: config.config?.username,
                  password: config.config?.password,
                  database: config.config?.database,
                  databases: config.config?.databases || [],
                  usePgDumpAll: config.use_pg_dump_all || config.config?.use_pg_dump_all || false,
                  backupGlobalsOnly: config.config?.backup_globals_only || false,
                }
              : {
                  paths: filePaths,
                  useVSS: config.config?.vss?.enabled || config.use_vss || false,
                }),
          };
          setData(newData);
          setInitialDataSnapshot(JSON.stringify(newData));
        })
        .catch(async (err) => {
          console.error('Failed to fetch config:', err);
          await showError(t('errors.title'), t('errors.configLoadFailed'));
          onClose();
        })
        .finally(() => setFetchingConfig(false));
    }
  }, [isEditMode, editConfigId, isOpen, onClose]);

  const steps = getSteps(data.type, t);
  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

  const handleChange = (newData: unknown) => {
    setData({ ...data, ...(newData as Partial<WizardData>) });
  };

  const handleNext = () => {
    if (currentStep === 'name' && !data.name.trim()) {
      void showError(t('simpleWizard.errors.title'), t('simpleWizard.errors.nameRequired'));
      return;
    }

    if (currentStep === 'source' && !data.agent_id) {
      void showError(t('simpleWizard.errors.title'), t('simpleWizard.errors.agentRequired'));
      return;
    }

    if (currentStepIndex < steps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCompletedSteps((prev) => new Set([...prev, nextIndex]));
      setCurrentStep(steps[nextIndex].id);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  const handleSubmit = async () => {
    if (isEditMode && !hasUnsavedChanges) return;

    setLoading(true);

    // Validate storage destination - auto-assign PLATFORM storage if not selected
    if (!data.storage_destination_id) {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch('/api/v1/storage', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const storages = await res.json();
          const platformStorage = storages.find((s: { type: string }) => s.type === 'PLATFORM');
          if (platformStorage) {
            setData((prev) => ({ ...prev, storage_destination_id: platformStorage.id }));
          } else {
            await showError(t('errors.title'), t('storage.platformNotAvailable'));
            setLoading(false);
            return;
          }
        } else {
          await showError(t('errors.title'), t('storage.fetchFailed'));
          setLoading(false);
          return;
        }
      } catch (_e) {
        await showError(t('errors.title'), t('storage.validateFailed'));
        setLoading(false);
        return;
      }
    }
    try {
      const token = localStorage.getItem('auth_token');

      // Ensure created_by is never empty - fallback to current user
      const effectiveCreatedBy = data.created_by || user?.id;

      const payload = {
        name: data.name || `${data.type} Backup`,
        type: data.type,
        agent_id: data.agent_id,
        created_by: effectiveCreatedBy,
        storage_destination_id: data.storage_destination_id,
        run_missing_jobs_on_startup: data.run_missing_jobs_on_startup,
        retention_policy: data.retention_policy || {
          keep_daily: 7,
          keep_weekly: 4,
          keep_monthly: 6,
          keep_last: 0,
        },
        config:
          data.type === 'POSTGRESQL'
            ? {
                host: data.host,
                port: data.port,
                username: data.username,
                password: data.password,
                database:
                  data.usePgDumpAll || data.backupGlobalsOnly
                    ? ''
                    : data.databases?.[0] || data.database || '',
                databases: data.usePgDumpAll || data.backupGlobalsOnly ? [] : data.databases || [],
                use_pg_dump_all: data.usePgDumpAll || false,
                backup_globals_only: data.backupGlobalsOnly || false,
              }
            : {
                paths: data.paths || [],
                vss: {
                  enabled: data.useVSS || false,
                },
              },
        schedule: (() => {
          // Convert frontend schedule types to backend format
          const {
            schedule_type,
            time,
            day_of_week,
            day_of_month,
            interval_minutes,
            cron_expression,
            timezone,
          } = data;

          // Default to browser timezone or UTC
          const scheduleTimezone =
            timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

          if (schedule_type === 'MINUTELY') {
            return {
              type: 'INTERVAL',
              interval_value: `${interval_minutes || 15}m`,
              timezone: scheduleTimezone,
            };
          }

          if (schedule_type === 'CUSTOM') {
            return {
              type: 'CRON',
              cron_expression: cron_expression || '0 0 * * *',
              timezone: scheduleTimezone,
            };
          }

          // For DAILY, WEEKLY, MONTHLY - convert to cron expression
          const [hours = '00', minutes = '00'] = (time || '00:00').split(':');
          let cronExpr = '';

          switch (schedule_type) {
            case 'DAILY':
              cronExpr = `${minutes} ${hours} * * *`;
              break;
            case 'WEEKLY':
              cronExpr = `${minutes} ${hours} * * ${day_of_week || 0}`;
              break;
            case 'MONTHLY':
              cronExpr = `${minutes} ${hours} ${day_of_month || 1} * *`;
              break;
            default:
              cronExpr = '0 0 * * *';
          }

          return {
            type: 'CRON',
            cron_expression: cronExpr,
            timezone: scheduleTimezone,
          };
        })(),
      };

      const url = isEditMode
        ? `/api/v1/backups/configs/${editConfigId}`
        : '/api/v1/backups/configs';
      const method = isEditMode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        // Save alert settings
        const configData = await res.json();
        const configId = isEditMode ? editConfigId : configData.id;

        if (configId && data.alert_settings) {
          try {
            const alertRes = await fetch(`/api/v1/backups/configs/${configId}/alert-settings`, {
              method: 'PUT',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                grace_period_minutes: data.alert_settings.grace_period_minutes || 10,
                throttle_interval_minutes: data.alert_settings.throttle_interval_minutes || 60,
              }),
            });

            if (!alertRes.ok) {
              console.error(
                'Failed to save alert settings:',
                alertRes.status,
                await alertRes.text()
              );
            }
          } catch (e) {
            console.error('Failed to save alert settings:', e);
          }
        }

        onSuccess?.();
        onClose();
      } else {
        const error = await res.json();
        await showError(
          t('errors.title'),
          `${isEditMode ? t('errors.updateFailed') : t('simpleWizard.errors.createFailed')}: ${error.message || t('errors.unknown')}`
        );
      }
    } catch (e) {
      console.error(e);
      await showError(
        t('errors.title'),
        isEditMode ? t('errors.updateFailed') : t('simpleWizard.errors.createFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'name':
        return (
          <Name
            data={data}
            onChange={handleChange}
            onNext={handleNext}
            onBack={handleBack}
            editConfigId={editConfigId}
            userRole={user?.role}
          />
        );
      case 'source':
        return (
          <SourceSelection
            data={data}
            onChange={handleChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'credentials':
        return (
          <Credentials
            data={data}
            onChange={handleChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'schedule':
        return <Schedule data={data} onChange={handleChange} />;
      case 'storage':
        return (
          <Storage data={data} onChange={handleChange} onNext={handleNext} onBack={handleBack} />
        );
      case 'retention':
        return (
          <Retention data={data} onChange={handleChange} onNext={handleNext} onBack={handleBack} />
        );
      case 'alerts':
        return (
          <AlertSettings
            data={data}
            onChange={handleChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'preview':
        return (
          <Preview
            data={data}
            isEditing={isEditMode}
            onConfirm={handleSubmit}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    const prefix = isEditMode ? t('wizard.buttons.editPrefix') : '';
    switch (currentStep) {
      case 'name':
        return `${prefix}${t('wizard.steps.nameDescription')}`;
      case 'source':
        return `${prefix}${t('wizard.steps.sourceDescription')}`;
      case 'credentials':
        return data.type === 'FILES'
          ? `${prefix}${t('wizard.steps.fileSourceDescription')}`
          : `${prefix}${t('wizard.steps.credentialsDescription')}`;
      case 'schedule':
        return `${prefix}${t('wizard.steps.scheduleDescription')}`;
      case 'storage':
        return `${prefix}${t('wizard.steps.storageDescription')}`;
      case 'retention':
        return `${prefix}${t('wizard.steps.retentionDescription')}`;
      case 'alerts':
        return `${prefix}${t('wizard.steps.alertsDescription')}`;
      case 'preview':
        return `${prefix}${t('wizard.steps.previewDescription')}`;
      default:
        return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="backup-config-wizard-title"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-2 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
    >
      <Card className="my-auto w-full max-w-[900px] h-[85dvh] max-h-[calc(100dvh-1rem)] overflow-hidden shadow-elevated flex flex-col">
        <div className="p-4 sm:p-6 border-b border-surface-border-light dark:border-surface-border-dark">
          <div className="flex items-center justify-between mb-4">
            <h2
              id="backup-config-wizard-title"
              className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight min-w-0"
            >
              {getStepTitle()}
            </h2>
            <button
              onClick={onClose}
              aria-label={t('common:actions.close')}
              title={t('common:actions.close')}
              className="!min-h-11 !min-w-11 shrink-0 flex items-center justify-center p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl transition-colors"
            >
              <X size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
            </button>
          </div>
          <WizardSteps
            steps={steps}
            currentStep={currentStepIndex}
            completedSteps={completedSteps}
            progressLabel={t('wizard.steps.progressLabel')}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
          <div key={currentStep} className="animate-in fade-in slide-in-from-right-4 duration-300">
            {renderStep()}
          </div>
        </div>

        <div className="p-4 sm:p-6 border-t border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark">
          <div className="flex items-center justify-between">
            <Button
              variant="secondary"
              onClick={currentStep === 'name' ? onClose : handleBack}
              disabled={loading}
            >
              {currentStep === 'name' ? (
                <>
                  <X size={16} className="mr-2" />
                  {t('wizard.buttons.cancel')}
                </>
              ) : (
                <>
                  <ArrowLeft size={16} className="mr-2" />
                  {t('wizard.buttons.back')}
                </>
              )}
            </Button>

            {fetchingConfig ? (
              <Button disabled className="min-w-[140px]">
                <Loader2 size={16} className="mr-2 animate-spin" />
                {t('wizard.buttons.loading')}
              </Button>
            ) : currentStep !== 'preview' ? (
              <Button onClick={handleNext} disabled={loading} className="min-w-[120px]">
                {t('wizard.buttons.next')}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || (isEditMode && !hasUnsavedChanges)}
                className="min-w-[140px] bg-state-ok-light hover:bg-state-ok-light/90 shadow-lg shadow-state-ok-light/20 dark:shadow-none"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {isEditMode ? t('schedules.updated') : t('simpleWizard.buttons.creating')}
                  </>
                ) : (
                  <>
                    <Check size={16} className="mr-2" />
                    {isEditMode
                      ? t('wizard.buttons.saveChanges')
                      : t('wizard.preview.createButton')}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
